import {
  createCheckoutSession,
  createOrvalRequestOptions,
  confirmPayment as confirmPortalPayment,
  finishCheckoutSession,
  getPortalPaymentProcess,
  listPortalPayments,
  resumeCheckoutSession,
} from "@token/shared";
import type {
  CheckoutSessionResponseDto,
  ConfirmPaymentRequestDto,
  CreateCheckoutSessionRequestDto,
  PaymentDto,
  PaymentProcessInfoDto,
} from "@token/shared";
import type { ApiClient } from "@/lib/api";
import { PaymentProviderName } from "@/lib/config";
import type {
  PaymentApi,
  PaymentConfirmation,
  PortalPayment,
  PaymentProcessInfo,
  PaymentSession,
  TokenPurchaseOrder,
} from "./types";

export type PaymentApiMode = "http" | "mock";

export interface CreatePaymentApiOptions {
  mode?: PaymentApiMode;
}

export function createPaymentApi(
  api: ApiClient,
  opts: CreatePaymentApiOptions = {},
): PaymentApi {
  if (opts.mode === "mock") {
    return new MockPaymentApi(api);
  }
  return new HttpPaymentApi(api);
}

class HttpPaymentApi implements PaymentApi {
  constructor(private readonly api: ApiClient) {}

  async createSession(order: TokenPurchaseOrder): Promise<PaymentSession> {
    const payload: CreateCheckoutSessionRequestDto = {
      amountInMinorUnits: order.amountInMinorUnits,
      currency: order.currency,
      countryCode: order.countryCode,
      usTaxConfirmation: order.usTaxConfirmation,
    };
    const response = await createCheckoutSession(
      payload,
      createOrvalRequestOptions(this.api),
    );
    return toPaymentSession(response.data);
  }

  async getSession(paymentId: string): Promise<PaymentSession> {
    const response = await resumeCheckoutSession(
      { paymentSessionId: paymentId },
      createOrvalRequestOptions(this.api),
    );
    return toPaymentSession(response.data);
  }

  async getProcessInfo(): Promise<PaymentProcessInfo> {
    const response = await getPortalPaymentProcess(
      createOrvalRequestOptions(this.api),
    );
    return toProcessInfo(response.data);
  }

  async listPayments(): Promise<PortalPayment[]> {
    const response = await listPortalPayments(
      createOrvalRequestOptions(this.api),
    );
    return response.data.map(toPortalPayment);
  }

  async finishCheckoutSession(paymentProviderSessionId: string): Promise<void> {
    await finishCheckoutSession(
      { paymentProviderSessionId },
      createOrvalRequestOptions(this.api),
    );
  }

  async confirmPayment(confirmation: PaymentConfirmation): Promise<void> {
    await confirmPortalPayment(
      toConfirmPaymentRequest(confirmation),
      createOrvalRequestOptions(this.api),
    );
  }
}

class MockPaymentApi implements PaymentApi {
  constructor(private readonly api: ApiClient) {}

  async createSession(order: TokenPurchaseOrder): Promise<PaymentSession> {
    await delay();
    const sessionId = `mock-session-${order.orderReference}`;
    return {
      paymentSessionId: sessionId,
      sessionId,
      sessionData: `mock-session-data-${sessionId}`,
      clientKey: "mock-client-key",
      orderReference: order.orderReference,
      returnUrl: new URL("/payment/return", window.location.origin).toString(),
      expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
    };
  }

  async getSession(paymentId: string): Promise<PaymentSession> {
    await delay();
    return {
      paymentSessionId: paymentId,
      sessionId: paymentId,
      sessionData: `mock-session-data-${paymentId}`,
      clientKey: "mock-client-key",
      returnUrl: new URL("/payment/return", window.location.origin).toString(),
    };
  }

  async getProcessInfo(): Promise<PaymentProcessInfo> {
    await delay();
    return {
      minimumAmountInMinorUnits: 1000,
      maximumAmountInMinorUnits: 10000000,
      currency: "EUR",
      countryCode: "AT",
      conversionFactor: 1,
      taxRate: 20,
    };
  }

  async listPayments(): Promise<PortalPayment[]> {
    await delay();
    return [];
  }

  async finishCheckoutSession(paymentProviderSessionId: string): Promise<void> {
    await finishCheckoutSession(
      { paymentProviderSessionId },
      createOrvalRequestOptions(this.api),
    );
  }

  async confirmPayment(confirmation: PaymentConfirmation): Promise<void> {
    await confirmPortalPayment(
      toConfirmPaymentRequest(confirmation),
      createOrvalRequestOptions(this.api),
    );
  }
}

function toPaymentSession(dto: CheckoutSessionResponseDto): PaymentSession {
  if (!dto.sessionId) {
    throw new Error("Die Payment Session enthält keine sessionId.");
  }
  if (!dto.returnUrl) {
    throw new Error("Die Payment Session enthält keine returnUrl.");
  }
  return {
    paymentSessionId: paymentSessionIdFromDto(dto),
    sessionId: dto.sessionId,
    sessionData: dto.sessionData,
    clientKey: dto.clientKey,
    checkoutUrl: checkoutUrlFromSessionData(dto.sessionData),
    orderReference: dto.orderReference,
    returnUrl: dto.returnUrl,
    expiresAt: dto.expiresAt,
  };
}

function paymentSessionIdFromDto(
  dto: CheckoutSessionResponseDto,
): string | undefined {
  const raw = dto as CheckoutSessionResponseDto & {
    id?: string;
    paymentSessionId?: string;
    paymentId?: string;
  };
  return raw.paymentSessionId ?? raw.id ?? raw.paymentId;
}

function checkoutUrlFromSessionData(sessionData: string | undefined): string | undefined {
  if (!sessionData) return undefined;
  if (isHttpUrl(sessionData)) return sessionData;

  try {
    const parsed = JSON.parse(sessionData) as unknown;
    if (!isRecord(parsed)) return undefined;
    const checkoutUrl = parsed.checkoutUrl ?? parsed.url;
    return typeof checkoutUrl === "string" && isHttpUrl(checkoutUrl)
      ? checkoutUrl
      : undefined;
  } catch {
    return undefined;
  }
}

function isHttpUrl(value: string): boolean {
  return value.startsWith("https://") || value.startsWith("http://");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toProcessInfo(dto: PaymentProcessInfoDto): PaymentProcessInfo {
  return {
    minimumAmountInMinorUnits: dto.minimumAmountInMinorUnits ?? 0,
    maximumAmountInMinorUnits: dto.maximumAmountInMinorUnits ?? 0,
    currency: dto.currency ?? "EUR",
    countryCode: dto.countryCode ?? "AT",
    conversionFactor: dto.conversionFactor ?? 1,
    taxRate: dto.taxRate ?? 0,
  };
}

function toPortalPayment(dto: PaymentDto): PortalPayment {
  return {
    id: dto.id,
    customerId: dto.customerId,
    orderId: dto.orderId,
    amountInCents: dto.amountInCents,
    taxAmountInCents: dto.taxAmountInCents,
    taxRate: dto.taxRate,
    conversionFactor: dto.conversionFactor,
    currency: dto.currency,
    countryCode: dto.countryCode,
    merchantAccount: dto.merchantAccount,
    returnUrl: dto.returnUrl,
    paymentProvider: dto.paymentProvider,
    paymentProviderSessionId: dto.paymentProviderSessionId,
    paymentProviderReference: dto.paymentProviderReference,
    paymentMethod: dto.paymentMethod,
    paymentInfo: dto.paymentInfo as Record<string, unknown> | undefined,
    status: dto.status,
    expiresAt: dto.expiresAt,
  };
}

function toConfirmPaymentRequest(
  confirmation: PaymentConfirmation,
): ConfirmPaymentRequestDto {
  const returnParameters = stringifyReturnParameters(confirmation.providerData);
  if (
    confirmation.provider === PaymentProviderName.Mock &&
    !returnParameters.status
  ) {
    returnParameters.status = statusFromOutcome(confirmation.outcome);
  }
  return {
    paymentSessionId: confirmation.sessionId,
    returnParameters,
  };
}

function stringifyReturnParameters(
  value: Record<string, unknown>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(value).flatMap(([key, item]) => {
      if (isPaymentSessionIdentifierKey(key)) return [];
      const stringValue = firstStringValue(item);
      return stringValue === undefined ? [] : [[key, stringValue]];
    }),
  );
}

function isPaymentSessionIdentifierKey(key: string): boolean {
  return (
    key === "paymentSessionId" ||
    key === "sessionId" ||
    key === "paymentProviderSessionId"
  );
}

function firstStringValue(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.find((item): item is string => typeof item === "string");
  }
  return undefined;
}

function statusFromOutcome(outcome: PaymentConfirmation["outcome"]): string {
  if (outcome === "failed") return "FAILED";
  if (outcome === "pending") return "PENDING";
  return "AUTHORISED";
}

function delay(ms = 120): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
